import axios from 'axios';

// Define the base URL for API requests
const API_URL = process.env.REACT_APP_API_URL || 'https://pikagpu.elliotliu.com/api';

// Create an axios instance with baseURL
const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10000, // 10 seconds timeout for requests
});

// Add a response interceptor for better error logging
apiClient.interceptors.response.use(
  response => response,
  error => {
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      console.error('API Error Response:', {
        status: error.response.status,
        data: error.response.data,
        headers: error.response.headers,
      });
    } else if (error.request) {
      // The request was made but no response was received
      console.error('API Error Request:', error.request);
    } else {
      // Something happened in setting up the request that triggered an Error
      console.error('API Error Setup:', error.message);
    }
    return Promise.reject(error);
  }
);

// API services
const apiService = {
  // Get all nodes and their GPU stats (for dashboard)
  getGpuStats: async () => {
    try {
      const response = await apiClient.get('/gpu-stats');
      return response.data;
    } catch (error) {
      console.error('Error fetching node stats:', error);
      throw error;
    }
  },

  // Get list of all nodes with optional filtering and sorting
  getNodes: async (filters = {}) => {
    try {
      const { user, cluster, version, sortBy } = filters;
      let url = '/nodes';
      
      // Build query parameters
      const params = new URLSearchParams();
      if (user) params.append('user', user);
      if (cluster) params.append('cluster', cluster);
      if (version) params.append('version', version);
      if (sortBy) params.append('sort_by', sortBy);
      
      // Append parameters to URL if any exist
      const queryString = params.toString();
      if (queryString) {
        url += `?${queryString}`;
      }
      
      const response = await apiClient.get(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching nodes:', error);
      throw error;
    }
  },

  // Get details for a specific node
  getNodeDetails: async (nodeId) => {
    try {
      const response = await apiClient.get(`/nodes/${nodeId}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching node details for ${nodeId}:`, error);
      throw error;
    }
  },

  // Get recent node history (last 10 minutes)
  getNodeHistory: async (nodeId) => {
    try {
      const response = await apiClient.get(`/nodes/${nodeId}/history`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching recent history for node ${nodeId}:`, error);
      throw error;
    }
  },

  // Get long-term summarized node history
  getNodeLongHistory: async (nodeId, hours = 24) => {
    try {
      const response = await apiClient.get(`/nodes/${nodeId}/long-history?hours=${hours}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching long-term history for node ${nodeId}:`, error);
      throw error;
    }
  },

  // Get cluster overview with optional filtering
  getClusters: async (filters = {}) => {
    try {
      const { user, version } = filters;
      let url = '/clusters';
      
      // Build query parameters
      const params = new URLSearchParams();
      if (user) params.append('user', user);
      if (version) params.append('version', version);
      
      // Append parameters to URL if any exist
      const queryString = params.toString();
      if (queryString) {
        url += `?${queryString}`;
      }
      
      const response = await apiClient.get(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching cluster overview:', error);
      throw error;
    }
  },
  
  // Get available filter options
  getFilterOptions: async () => {
    try {
      console.log('Fetching filter options from API...');
      const response = await apiClient.get('/api/filters');
      console.log('Received filter options:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching filter options:', error);
      throw error;
    }
  },
};

export default apiService; 